<script setup lang="ts">
import { onMounted } from 'vue'
import { useStore } from 'vuex'
import { consumeNavFeedback } from '@/composables/useDashboardNavFeedback'
import { useUpgradeModal } from '@/composables/useUpgradeModal'

const DONOR_HELP_CENTRE_URL = 'https://www.swiftaid.co.uk/guide/donor/'
const DONOR_MESSAGE_URL =
  'https://www.swiftaid.co.uk/donors/manage-account/something-else/'
const SUPPORT_EMAIL = 'support@swiftaid.co.uk'
const FAQ_GIFT_AID_URL =
  'https://www.swiftaid.co.uk/guide/donor/questions-about-gift-aid/what-is-gift-aid/'
const FAQ_SWIFTAID_DECLARATIONS_URL =
  'https://www.swiftaid.co.uk/guide/donor/questions-about-swiftaid/how-does-swiftaid-create-a-declaration-on-my-behalf/'

const store = useStore()
const { openUpgradeModal } = useUpgradeModal()

onMounted(() => {
  consumeNavFeedback()
  store.commit('SET_LOADING', false)
})

type ContactLink = {
  id: string
  iconClass: string
  titleKey: string
  subKey: string
  href: string
  external?: boolean
}

const contactLinks: ContactLink[] = [
  {
    id: 'help-centre',
    iconClass: 'sa-support-icon--blue',
    titleKey: 'Dashboard.Support.HelpCentre.Title',
    subKey: 'Dashboard.Support.HelpCentre.Sub',
    href: DONOR_HELP_CENTRE_URL,
    external: true,
  },
  {
    id: 'send-message',
    iconClass: 'sa-support-icon--teal',
    titleKey: 'Dashboard.Support.Chat.Title',
    subKey: 'Dashboard.Support.Chat.Sub',
    href: DONOR_MESSAGE_URL,
    external: true,
  },
  {
    id: 'email',
    iconClass: 'sa-support-icon--green',
    titleKey: 'Dashboard.Support.Email.Title',
    subKey: 'Dashboard.Support.Email.Sub',
    href: `mailto:${SUPPORT_EMAIL}`,
  },
]

type FaqLinkItem = {
  id: string
  kind: 'link'
  titleKey: string
  subKey: string
  href: string
}

type FaqRouteItem = {
  id: string
  kind: 'route'
  titleKey: string
  subKey: string
  routeName: 'ClaimTaxBack'
}

type FaqActionItem = {
  id: string
  kind: 'action'
  titleKey: string
  subKey: string
  action: 'upgrade'
}

type FaqItem = FaqLinkItem | FaqRouteItem | FaqActionItem

const faqItems: FaqItem[] = [
  {
    id: 'gift-aid',
    kind: 'link',
    titleKey: 'Dashboard.Support.Faq.GiftAid.Title',
    subKey: 'Dashboard.Support.Faq.GiftAid.Sub',
    href: FAQ_GIFT_AID_URL,
  },
  {
    id: 'swiftaid-declarations',
    kind: 'link',
    titleKey: 'Dashboard.Support.Faq.SwiftaidDeclarations.Title',
    subKey: 'Dashboard.Support.Faq.SwiftaidDeclarations.Sub',
    href: FAQ_SWIFTAID_DECLARATIONS_URL,
  },
  {
    id: 'higher-rate',
    kind: 'route',
    titleKey: 'Dashboard.Support.Faq.HigherRate.Title',
    subKey: 'Dashboard.Support.Faq.HigherRate.Sub',
    routeName: 'ClaimTaxBack',
  },
  {
    id: 'paid-plan',
    kind: 'action',
    titleKey: 'Dashboard.Support.Faq.PaidPlan.Title',
    subKey: 'Dashboard.Support.Faq.PaidPlan.Sub',
    action: 'upgrade',
  },
]

const contactLinkIcon = (id: string) => {
  if (id === 'help-centre') return '📖'
  if (id === 'send-message') return '💬'
  return '✉️'
}

const onFaqAction = (item: FaqActionItem) => {
  if (item.action === 'upgrade') {
    openUpgradeModal()
  }
}
</script>

<template>
  <div class="sa-g2" role="main">
    <div>
      <h2 class="sa-support-section-title">{{ $t('Dashboard.Support.HowCanWeHelp') }}</h2>
      <div class="sa-card sa-card--flush">
        <a
          v-for="link in contactLinks"
          :key="link.id"
          class="sa-support-row"
          :href="link.href"
          :target="link.external ? '_blank' : undefined"
          :rel="link.external ? 'noopener noreferrer' : undefined"
        >
          <span class="sa-support-icon" :class="link.iconClass" aria-hidden="true">
            {{ contactLinkIcon(link.id) }}
          </span>
          <span>
            <div class="sa-support-title">{{ $t(link.titleKey) }}</div>
            <div class="sa-support-sub">{{ $t(link.subKey) }}</div>
          </span>
          <svg
            class="sa-support-row__chevron"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </a>
      </div>
    </div>
    <div>
      <h2 class="sa-support-section-title">{{ $t('Dashboard.Support.CommonQuestions') }}</h2>
      <div class="sa-card sa-card--flush">
        <template v-for="item in faqItems" :key="item.id">
          <a
            v-if="item.kind === 'link'"
            class="sa-support-row"
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>
              <div class="sa-support-title">{{ $t(item.titleKey) }}</div>
              <div class="sa-support-sub">{{ $t(item.subKey) }}</div>
            </span>
            <svg
              class="sa-support-row__chevron"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </a>
          <router-link
            v-else-if="item.kind === 'route'"
            class="sa-support-row"
            :to="{ name: item.routeName }"
          >
            <span>
              <div class="sa-support-title">{{ $t(item.titleKey) }}</div>
              <div class="sa-support-sub">{{ $t(item.subKey) }}</div>
            </span>
            <svg
              class="sa-support-row__chevron"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </router-link>
          <button
            v-else-if="item.kind === 'action'"
            type="button"
            class="sa-support-row"
            @click="onFaqAction(item)"
          >
            <span>
              <div class="sa-support-title">{{ $t(item.titleKey) }}</div>
              <div class="sa-support-sub">{{ $t(item.subKey) }}</div>
            </span>
            <svg
              class="sa-support-row__chevron"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </template>
      </div>
    </div>
  </div>
</template>
